/** Coquille de l'application : bandeau supérieur, sélecteur de vue, thème. */

import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { PHASE_LABEL } from '../core/index.js';
import { icone, type IconName } from './icons.js';
import { store, type Role, type ThemeChoice } from './store.js';
import './views/home.js';
import './views/choix.js';
import './views/bureau.js';
import './views/isoloir.js';
import './views/affichage.js';
import './views/vote.js';
import './views/pv-view.js';

type NomRoute = 'home' | 'bureau' | 'isoloir' | 'affichage' | 'vote' | 'pv';

interface Route {
  readonly name: NomRoute;
  readonly electionId: string | null;
}

const ROUTES: readonly NomRoute[] = ['bureau', 'isoloir', 'affichage', 'vote', 'pv'];

const THEMES: readonly { valeur: ThemeChoice; icone: IconName; titre: string }[] = [
  { valeur: 'auto', icone: 'auto', titre: 'Thème automatique, selon le système' },
  { valeur: 'light', icone: 'soleil', titre: 'Thème clair' },
  { valeur: 'dark', icone: 'lune', titre: 'Thème sombre' },
];

/**
 * Les deux vues de l'enseignant, permutables d'un geste.
 *
 * « Projection » n'est pas un retour en arrière ni un simple agrandissement :
 * c'est la vue destinée à l'écran de la classe, qui applique d'office la
 * typographie de projection. Un seul sélecteur, donc, et pas deux réglages
 * qui se chevauchent.
 */
const VUES_ENSEIGNANT: readonly { cle: NomRoute; libelle: string; icone: IconName }[] = [
  { cle: 'bureau', libelle: 'Bureau de vote', icone: 'ecole' },
  { cle: 'affichage', libelle: 'Projection', icone: 'affichage' },
];

const ROLE_LABEL: Record<Role, string> = {
  bureau: 'Espace enseignant',
  isoloir: 'Isoloir',
  affichage: 'Affichage',
};

function parseRoute(): Route {
  const brut = location.hash.replace(/^#\/?/, '');
  const [nom, identifiant] = brut.split('/');
  if (nom !== undefined && (ROUTES as readonly string[]).includes(nom)) {
    return { name: nom as NomRoute, electionId: identifiant && identifiant.length > 0 ? identifiant : null };
  }
  return { name: 'home', electionId: null };
}

@customElement('app-root')
export class AppRoot extends LitElement {
  @state() private route: Route = parseRoute();
  @state() private theme: string = store.theme;

  /**
   * Comme les vues, la coquille rend dans le DOM clair : la feuille globale
   * doit pouvoir l'atteindre (voir `StoreElement`).
   */
  protected override createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  private readonly surChangement = (): void => {
    this.route = parseRoute();
    void this.synchroniser();
  };

  private readonly surMagasin = (): void => {
    this.theme = store.theme;
    this.requestUpdate();
  };

  private detach: (() => void) | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener('hashchange', this.surChangement);
    this.detach = store.subscribe(this.surMagasin);
    void this.synchroniser();
  }

  override disconnectedCallback(): void {
    window.removeEventListener('hashchange', this.surChangement);
    this.detach?.();
    this.detach = null;
    super.disconnectedCallback();
  }

  /** La vue de projection est toujours en grand, quelle que soit la machine. */
  override updated(): void {
    document.body.classList.toggle('projection', this.route.name === 'affichage');
  }

  /** Ouvre l'élection attendue par la route, ou choisit la seule disponible. */
  private async synchroniser(): Promise<void> {
    const { name, electionId } = this.route;
    if (name === 'home') {
      if (store.electionId !== null) store.close();
      return;
    }
    // Le vote en ligne est autonome : il n'ouvre aucune élection locale, il ne
    // parle qu'au relais. Aucune élection ne doit donc être chargée ici.
    if (name === 'vote') {
      if (store.electionId !== null) store.close();
      return;
    }

    let cible = electionId;
    if (cible === null) {
      const resumes = store.summaries();
      if (resumes.length === 1) {
        cible = resumes[0]?.id ?? null;
        if (cible !== null) {
          location.hash = `#/${name}/${cible}`;
          return;
        }
      }
    }

    if (cible !== null && cible !== store.electionId) {
      const role: Role = name === 'pv' ? 'bureau' : (name as Role);
      await store.open(cible, role);
    }
  }

  private choisirTheme(theme: ThemeChoice): void {
    store.setTheme(theme);
    this.theme = theme;
  }

  /** Identifiant de l'élection courante, pour construire les liens de vue. */
  private get electionId(): string {
    return this.route.electionId ?? store.electionId ?? '';
  }

  /** Le sélecteur de vue n'a de sens que dans l'espace enseignant. */
  private montrerVues(): boolean {
    return (this.route.name === 'bureau' || this.route.name === 'affichage' || this.route.name === 'pv') && this.electionId.length > 0;
  }

  private renderVue() {
    const { name } = this.route;
    if (name === 'home') return html`<view-home></view-home>`;
    if (name === 'vote') return html`<view-vote></view-vote>`;

    if (store.electionId === null) {
      const role: Role = name === 'pv' ? 'bureau' : (name as Role);
      return html`<view-choix .espace=${role}></view-choix>`;
    }

    switch (name) {
      case 'bureau':
        return html`<view-bureau></view-bureau>`;
      case 'isoloir':
        return html`<view-isoloir></view-isoloir>`;
      case 'affichage':
        return html`<view-affichage></view-affichage>`;
      case 'pv':
        return html`<view-pv></view-pv>`;
      default:
        return html`<view-home></view-home>`;
    }
  }

  override render() {
    const { name } = this.route;
    const phase = store.electionId !== null ? PHASE_LABEL[store.state.phase] : null;
    const vues = this.montrerVues();
    const electionId = this.electionId;

    return html`
      <div class="shell">
        <header class="topbar no-print ${vues ? 'topbar--vues' : ''}">
          <a class="topbar__brand" href="#/" title="Revenir à l’accueil">
            <span class="topbar__logo" aria-hidden="true">${icone('check', 19)}</span>
            <span class="topbar__nom">election.nsi.xyz</span>
          </a>

          ${vues
            ? html`<div class="segmented segmented--vues" role="group" aria-label="Vue affichée">
                ${VUES_ENSEIGNANT.map(
                  (vue) => html`
                    <button
                      type="button"
                      class="segmented__item segmented__item--large"
                      aria-pressed=${name === vue.cle ? 'true' : 'false'}
                      aria-label=${vue.libelle}
                      title=${vue.libelle}
                      @click=${() => {
                        if (name !== vue.cle) location.hash = `#/${vue.cle}/${electionId}`;
                      }}
                    >
                      ${icone(vue.icone, 18)}<span class="segmented__nom">${vue.libelle}</span>
                    </button>
                  `,
                )}
              </div>`
            : nothing}

          <span class="topbar__spacer"></span>

          ${phase !== null ? html`<span class="topbar__badge">${phase}</span>` : nothing}

          <div class="segmented" role="group" aria-label="Thème d’affichage">
            ${THEMES.map(
              (choix) => html`
                <button
                  type="button"
                  class="segmented__item"
                  title=${choix.titre}
                  aria-label=${choix.titre}
                  aria-pressed=${this.theme === choix.valeur ? 'true' : 'false'}
                  @click=${() => this.choisirTheme(choix.valeur)}
                >
                  ${icone(choix.icone, 19)}
                </button>
              `,
            )}
          </div>
        </header>
        ${this.renderVue()}
      </div>
    `;
  }
}

export { ROLE_LABEL };
